import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, of, shareReplay, catchError } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface ApiResponse<T> {
  status: boolean;
  message: string;
  data: T;
}

export interface Country {
  code: string;
  name: string;
  isEu?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface Region {
  code: string;
  name: string;
  countryCode: string;
}

export interface Province {
  code: string;
  name: string;
  regionCode: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Municipality {
  code: string;
  name: string;
  istatCode: string;
  isProvinceCapital?: boolean;
  provinceCode: string;
  createdAt?: string;
  updatedAt?: string;
}

@Injectable({
  providedIn: 'root'
})
export class GeographyService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.coreApiUrl}/api/v1`;

  /**
   * Recupera tutti i paesi o filtra per paesi UE
   * @param isEu - true per solo paesi UE, false per non-UE, undefined per tutti
   */
  getCountries(isEu?: boolean): Observable<Country[]> {
    const url = isEu !== undefined 
      ? `${this.baseUrl}/countries?isEu=${isEu}`
      : `${this.baseUrl}/countries`;
    
    return this.http.get<ApiResponse<Country[]>>(url).pipe(
      map(response => response.data || [])
    );
  }

  /**
   * Recupera le regioni, opzionalmente filtrate per codice paese
   * @param countryCode - Codice paese (es. 'IT')
   */
  getRegions(countryCode?: string): Observable<Region[]> {
    const url = countryCode
      ? `${this.baseUrl}/regions?countryCode=${countryCode}`
      : `${this.baseUrl}/regions`;
    
    return this.http.get<ApiResponse<Region[]>>(url).pipe(
      map(response => response.data || [])
    );
  }

  /**
   * Recupera le province, opzionalmente filtrate per codice regione
   * @param regionCode - Codice regione (es. 'LAZ')
   */
  getProvinces(regionCode?: string): Observable<Province[]> {
    const url = regionCode
      ? `${this.baseUrl}/provinces?regionCode=${regionCode}`
      : `${this.baseUrl}/provinces`;
    
    return this.http.get<ApiResponse<Province[]>>(url).pipe(
      map(response => response.data || [])
    );
  }

  /**
   * Recupera i comuni, opzionalmente filtrati per codice provincia
   * @param provinceCode - Codice provincia (es. 'RM')
   */
  getMunicipalities(provinceCode?: string): Observable<Municipality[]> {
    const url = provinceCode
      ? `${this.baseUrl}/municipalities?provinceCode=${provinceCode}`
      : `${this.baseUrl}/municipalities`;

    return this.http.get<ApiResponse<Municipality[]>>(url).pipe(
      map(response => response.data || [])
    );
  }

  // ── Decodifica comune (code → name) con cache per provincia ──────────────────
  // I comuni si caricano per provincia (nessun endpoint "tutti"): manteniamo una
  // cache per codice provincia così i punti di sola lettura possono risolvere il
  // nome del comune a partire dal codice salvato senza rifare fetch ripetute.
  private municipalitiesByProvince = new Map<string, Observable<Municipality[]>>();

  /**
   * Come getMunicipalities(provinceCode) ma con cache: la stessa provincia viene
   * richiesta al backend una sola volta e condivisa tra i chiamanti.
   */
  getMunicipalitiesCached(provinceCode: string): Observable<Municipality[]> {
    if (!provinceCode) {
      return of([]);
    }
    let cached = this.municipalitiesByProvince.get(provinceCode);
    if (!cached) {
      cached = this.getMunicipalities(provinceCode).pipe(
        catchError(() => of([] as Municipality[])),
        shareReplay(1),
      );
      this.municipalitiesByProvince.set(provinceCode, cached);
    }
    return cached;
  }

  /**
   * Risolve il nome del comune a partire dal codice salvato e dal codice provincia.
   * Se il valore non corrisponde ad alcun codice (es. dati legacy salvati come nome)
   * restituisce il valore originale, così la visualizzazione resta sensata.
   */
  resolveMunicipalityName(municipalityValue: string | null | undefined, provinceCode: string | null | undefined): Observable<string> {
    const value = (municipalityValue || '').trim();
    if (!value || !provinceCode) {
      return of(value);
    }
    return this.getMunicipalitiesCached(provinceCode).pipe(
      map(municipalities => municipalities.find(m => m.code === value)?.name || value),
    );
  }
}
